import React, { FC, useCallback, useMemo, useState } from 'react';
import { View } from 'react-native';
import { KitchenDisplayMode } from '@oolio-group/domain';
import {
  useTranslation,
  supportedLocales,
  Locale,
} from '@oolio-group/localization';
import {
  POSTHOG_EVENT_ACTIONS,
  POSTHOG_EVENT_DATA,
  analyticsService,
} from '../../analytics/AnalyticsService';
import { useSession } from '../../hooks/useSession';
import { useSettings } from '../../hooks/useSettings';
import { useRestart } from '../../hooks/useRestart';
import { useKitchenDisplay } from '../../hooks/useKitchenDisplay';
import SettingsItem from '../../component/SettingsItem/SettingsItem';
import SettingsAction from '../../component/SettingsItem/SettingsAction';
import SectionHeading from './SectionHeading';
import SettingsScreenStyles from './Settings.styles';

const DISPLAY_TYPE_OPTIONS = [
  {
    value: KitchenDisplayMode.PREP,
    labelKey: 'kitchenDisplay.displayTypePrep',
  },
  {
    value: KitchenDisplayMode.EXPO,
    labelKey: 'kitchenDisplay.displayTypeExpo',
  },
];

const GeneralSettings: FC = () => {
  const styles = SettingsScreenStyles();
  const { translate } = useTranslation();
  const { session, setSession } = useSession();
  const { logout } = useSettings();
  const { restartDevice } = useRestart();
  const { updateKitchenDisplay } = useKitchenDisplay({
    storeId: session?.currentStore?.id,
  });

  const [locale, setLocale] = useState(session?.localeCode);
  const [showLanguages, setShowLanguages] = useState<boolean>(false);
  const [showDisplayTypes, setShowDisplayTypes] = useState<boolean>(false);

  const currentMode = session?.kitchenDisplay?.mode || KitchenDisplayMode.PREP;

  const availableLocales = useMemo(
    () =>
      Object.values(supportedLocales).filter(
        locale =>
          !(
            locale.languageTag.includes('en-') && locale.languageTag !== 'en-US'
          ),
      ),
    [],
  );

  const currentLocale = availableLocales.find(
    language => language.languageTag == locale,
  );

  const onChangeLanguage = (language: Locale) => {
    analyticsService.capture('Changed Language');
    setLocale(language.languageTag);
    setSession({ localeCode: language.languageTag });
    restartDevice();
  };

  const onChangeDisplayType = useCallback(
    (mode: KitchenDisplayMode) => {
      if (mode === session?.kitchenDisplay?.mode) return;
      analyticsService.capture('Changed Display Type');
      updateKitchenDisplay({
        id: session?.kitchenDisplay?.id || '',
        mode,
      });
      setSession({
        kitchenDisplay: {
          ...session.kitchenDisplay,
          mode,
        },
      });
    },
    [session.kitchenDisplay, setSession, updateKitchenDisplay],
  );

  const onPressLogout = useCallback(() => {
    analyticsService.capture(POSTHOG_EVENT_ACTIONS.LOGOUT, {
      device: session.kitchenDisplay?.id,
      kdsName: session?.kitchenDisplay?.name,
      store: session?.currentStore?.id,
      orgId: session?.currentOrganization?.id,
      orgName: session?.currentOrganization?.name,
    } as POSTHOG_EVENT_DATA);
    if (session?.kitchenDisplay?.id) logout(session?.kitchenDisplay?.id);
  }, [session, logout]);

  return (
    <View style={styles.panel} ph-label="settings-general">
      <View style={styles.panelSection}>
        <SectionHeading
          testID="heading-identity"
          title={translate('kitchenDisplay.identity')}
        />
        <SettingsItem
          type="text"
          testID="kds-name"
          title={translate('kitchenDisplay.displayName')}
          subtitle={session?.kitchenDisplay?.name}
        />
        <SettingsItem
          type="parent"
          testID="select-displayType"
          title={translate('kitchenDisplay.displayType')}
          checked={showDisplayTypes}
          subtitle={translate(
            currentMode === KitchenDisplayMode.EXPO
              ? 'kitchenDisplay.displayTypeExpo'
              : 'kitchenDisplay.displayTypePrep',
          )}
          onChange={() => setShowDisplayTypes(!showDisplayTypes)}
        />
        {showDisplayTypes ? (
          <View style={styles.subSection}>
            {DISPLAY_TYPE_OPTIONS.map(option => (
              <SettingsItem
                key={option.value}
                type="select"
                testID={`display-type-${option.value.toLowerCase()}`}
                title={translate(option.labelKey)}
                checked={currentMode === option.value}
                onChange={() => onChangeDisplayType(option.value)}
              />
            ))}
          </View>
        ) : null}
        <SettingsItem
          type="parent"
          testID="select-language"
          title={translate('kitchenDisplay.Language')}
          checked={showLanguages}
          subtitle={currentLocale?.label}
          onChange={() => setShowLanguages(!showLanguages)}
        />
        {showLanguages ? (
          <View style={styles.subSection}>
            {availableLocales?.map((locale, i) => (
              <SettingsItem
                key={i}
                type="select"
                // Keyed on the stable language tag rather than the
                // localized label, which changes with translations.
                testID={`language-${locale.languageTag.toLowerCase()}`}
                title={locale.label}
                checked={locale.languageTag === currentLocale?.languageTag}
                onChange={() => onChangeLanguage(locale)}
              />
            ))}
          </View>
        ) : null}
      </View>
      <View style={styles.panelSection}>
        <SectionHeading
          testID="heading-device"
          title={translate('kitchenDisplay.device')}
        />
        <SettingsAction
          type="negative"
          testID="btn-logout"
          onPress={onPressLogout}
          label={translate('kitchenDisplay.logout')}
        />
      </View>
    </View>
  );
};

export default GeneralSettings;
